export function drawShadow(renderContext, node, calllback) {
  const { context } = renderContext;
  const render = () => {
    calllback();
    resetShadow(renderContext);
  };
  if (!node.shadow || node.shadow === 'none') return render();
  const shadows = parseBoxShadowValue(node.shadow);
  const zoom = renderContext.providers.scale.scale;
  if (!shadows.length) {
    return render();
  }
  shadows.reverse().forEach((result) => {
    if (!result) return;
    context.shadowColor = result.color;
    context.shadowBlur = result.blur * zoom;
    context.shadowOffsetX = result.offsetX * zoom;
    context.shadowOffsetY = result.offsetY * zoom;
    context.shadowSpread = result.spread * zoom;
    render();
  });
}

export function resetShadow(renderContext) {
  const { context } = renderContext;
  context.shadowColor = null;
  context.shadowBlur = 0;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowSpread = 0;
}

const boxShadowRegExp = /[^\s(]+(\(.+\))?/g;
/// 1. 10px 5px 5px black;
/// 2. 2px 2px 2px 1px rgb(0 0 0 / 20%);
/// 3. rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.1) 0px 2px 4px -2px

export function parseBoxShadowValue(value) {
  if (!value) return [];
  // Split multiple shadow definitions by comma, but preserve commas inside rgba()
  const shadowStrings = [];
  let currentShadow = '';
  let inRGBA = false;

  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    if (char === '(' && (currentShadow.endsWith('rgba') || currentShadow.endsWith('rgb'))) {
      inRGBA = true;
    }
    if (inRGBA && char === ')') {
      inRGBA = false;
    }
    if (char === ',' && !inRGBA) {
      shadowStrings.push(currentShadow.trim());
      currentShadow = '';
      continue;
    }
    currentShadow += char;
  }
  if (currentShadow) {
    shadowStrings.push(currentShadow.trim());
  }

  return shadowStrings.map(parseSingleBoxShadowValue).filter((n) => !!n);
}

function parseSingleBoxShadowValue(value) {
  const result = value.match(boxShadowRegExp);
  if (result.length === 4) {
    const [offsetX, offsetY, blur, color] = result;
    return {
      offsetX: parseInt(offsetX),
      offsetY: parseInt(offsetY),
      blur: parseInt(blur),
      spread: 0,
      color,
    };
  } else if (result.length === 5) {
    if (result[0].includes('rgb') || result[0].includes('#')) {
      const [color, offsetX, offsetY, blur, spread] = result;
      return {
        offsetX: parseInt(offsetX),
        offsetY: parseInt(offsetY),
        blur: parseInt(blur),
        spread: parseInt(spread),
        color,
      };
    }
    const [offsetX, offsetY, blur, spread, color] = result;
    return {
      offsetX: parseInt(offsetX),
      offsetY: parseInt(offsetY),
      blur: parseInt(blur),
      spread: parseInt(spread),
      color,
    };
  } else {
    return null;
  }
}

export function resolveTextShadow(element, styles) {
  const dataShadow = element.getAttribute('data-shadow');
  if (dataShadow) return dataShadow;

  const textShadow = styles.textShadow;
  if (!textShadow || textShadow === 'none') {
    return styles.boxShadow;
  }

  const shadows = textShadow.split(/,\s*(?![^()]*\))/g);

  const validShadows = shadows
    .map((shadowStr) => {
      const tokens = splitShadowTokens(shadowStr.trim());
      if (tokens.length === 0) {
        return null;
      }

      let color = 'currentColor';
      let colorIndex = -1;

      // Check first token for color
      if (isColor(tokens[0])) {
        color = tokens[0];
        colorIndex = 0;
      }
      // Check last token for color if not found
      else if (tokens.length > 0 && isColor(tokens[tokens.length - 1])) {
        color = tokens[tokens.length - 1];
        colorIndex = tokens.length - 1;
      }

      // Extract numerical parts (offsetX, offsetY, blur)
      const numericalTokens = [];
      for (let i = 0; i < tokens.length; i++) {
        if (i !== colorIndex) {
          numericalTokens.push(tokens[i]);
        }
      }

      let offsetX, offsetY, blur;
      if (numericalTokens.length >= 2 && numericalTokens.length <= 3) {
        offsetX = numericalTokens[0];
        offsetY = numericalTokens[1];
        blur = numericalTokens.length === 3 ? numericalTokens[2] : '0';
      } else {
        return null; // Invalid shadow format
      }

      return { x: offsetX, y: offsetY, blur: blur, color: color };
    })
    .filter((shadow) => shadow !== null);

  return validShadows
    .map((shadow) => {
      return `${shadow.x} ${shadow.y} ${shadow.blur} ${shadow.color}`;
    })
    .join(', ');
}

function splitShadowTokens(shadowStr) {
  const tokens = [];
  let currentToken = '';
  let inParen = false;

  for (const char of shadowStr) {
    if (char === '(') {
      inParen = true;
      currentToken += char;
    } else if (char === ')') {
      inParen = false;
      currentToken += char;
    } else if (char === ' ' && !inParen) {
      if (currentToken) {
        tokens.push(currentToken);
        currentToken = '';
      }
    } else {
      currentToken += char;
    }
  }

  if (currentToken) {
    tokens.push(currentToken);
  }

  return tokens;
}

function isColor(token) {
  return token.startsWith('#') || token.startsWith('rgb(') || token.startsWith('rgba(');
}
